import { Suspense, useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  Link,
  matchRoutes,
} from "react-router-dom";
import routes, { RouteType } from "./route";
import ProtectedRoute from "./ProtectedRoute";
import { RouteErrorBoundary } from "./RouteErrorBoundary";
import { AuthStalledScreen } from "@/components";
import { COLORS, ROUTE_PATHS } from "@/constants";
import { isRouteDebugEnabled } from "@/utils/helper/route-debug";
import { reportError, trackEvent } from "@/utils/helper/error-reporter";
import {
  getCallbackDestination,
  isPublicCallbackDestination,
  unwrapLiffState,
} from "./callback-destination";
import { ensureTenantId } from "@/contexts/AuthContext";

// Route table for matchRoutes(): the same paths <Routes> below renders, so the
// Suspense key resolves to the PATTERN a location matches ("/trainer/details/:id"),
// not the concrete pathname. See AppRoutesContent.
const routePatterns = [
  ...routes.map(({ path }) => ({ path })),
  { path: "/" },
  { path: "*" },
];

const matchedRoutePattern = (pathname: string): string =>
  matchRoutes(routePatterns, pathname)?.[0]?.route.path ?? pathname;

// A lazy route chunk has no timeout: an import() that never settles (LINE
// webview network stall, dead connection after the app was backgrounded)
// would leave the fallback below on screen forever. Past this window the
// member gets the stalled screen with a reload, and we get a report.
const ROUTE_CHUNK_STALL_TIMEOUT_MS = 15_000;

// "tenantId,liff.state,next" — which params were present, never their values.
const describeSearchKeys = (search: string): string => {
  try {
    return Array.from(new URLSearchParams(search).keys()).join(",");
  } catch {
    return "";
  }
};

// Same for the URL fragment. On a LIFF primary redirect the fragment carries
// the member's credentials (#access_token=…&id_token=…&context_token=…), and
// LINE's guidelines forbid sending that URL to any logging tool — so beacons
// and debug screens only ever see the key names.
const describeHashKeys = (hash: string): string => {
  try {
    return Array.from(new URLSearchParams(hash.replace(/^#/, "")).keys()).join(",");
  } catch {
    return "";
  }
};

const AppRouteLoading = () => {
  const location = useLocation();
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    const timer = globalThis.setTimeout(() => {
      setStalled(true);
      reportError("Route chunk stalled", {
        extra: {
          stage: "route-chunk",
          pathname: location.pathname,
          timeoutMs: ROUTE_CHUNK_STALL_TIMEOUT_MS,
        },
      });
    }, ROUTE_CHUNK_STALL_TIMEOUT_MS);
    return () => globalThis.clearTimeout(timer);
  }, [location.pathname]);

  if (stalled) {
    return <AuthStalledScreen />;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",
        minHeight: "100dvh",
        color: COLORS.text.pagePrimary,
        backgroundColor: COLORS.background.bodyDefault,
      }}
    >
      <div>Loading...</div>
      {isRouteDebugEnabled && (
        <div style={{ fontSize: "14px", opacity: 0.75 }}>
          {`Requesting route: ${location.pathname}${location.search}${location.hash ? ` #${describeHashKeys(location.hash)}` : ""}`}
        </div>
      )}
    </div>
  );
};

// Shared debug visual (debug builds only). Heading is passed in so the root
// and not-found cases don't share a misleading title.
const DebugRouteScreen = ({ heading }: { heading: string }) => {
  const location = useLocation();
  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "12px",
        color: COLORS.common.white,
        padding: "24px",
        textAlign: "center",
      }}
    >
      <h1 style={{ margin: 0 }}>{heading}</h1>
      <div>{`Current route: ${location.pathname}${location.search}${location.hash ? ` #${describeHashKeys(location.hash)}` : ""}`}</div>
      <div
        style={{ opacity: 0.8 }}
      >{`Origin: ${globalThis.location.origin}`}</div>
      <Link
        to={ROUTE_PATHS.MEMBER.INFORMATION}
        style={{
          color: COLORS.common.white,
          border: `1px solid ${COLORS.common.white}`,
          padding: "10px 16px",
          borderRadius: "8px",
          textDecoration: "none",
        }}
      >
        Go to Member Page
      </Link>
    </div>
  );
};

// Only rendered by the "/" route, and only when isRouteDebugEnabled — so no
// internal flag guard is needed here (it would be dead code).
const RootPathDebugPage = () => {
  const location = useLocation();

  useEffect(() => {
    trackEvent("RootPathRendered", {
      pathname: location.pathname,
      hashKeys: describeHashKeys(location.hash),
      // Param names only: the LIFF return carries ?code=&state=&liff.state=
      // and rich-menu links carry tenant ids. Names still show what kind of
      // link it was; the path alone identifies a stale one.
      searchKeys: describeSearchKeys(location.search),
    });
  }, [location.hash, location.pathname, location.search]);

  return <DebugRouteScreen heading="Root Path Debug" />;
};

// Catch-all handler for unmatched paths. Reports the hit to Error Reporting
// UNCONDITIONALLY (unlike RouteRequested, which is debug-gated) so a stale
// rich-menu link or renamed route is visible in prod telemetry instead of a
// silent redirect. In debug builds it shows the debug page (matching the "/"
// route's escape hatch) so a developer can read the unknown path; otherwise it
// redirects the member to the profile hub.
const NotFoundRedirect = () => {
  const location = useLocation();

  useEffect(() => {
    trackEvent("RouteNotFound", {
      pathname: location.pathname,
      hashKeys: describeHashKeys(location.hash),
      // Param names only: the LIFF return carries ?code=&state=&liff.state=
      // and rich-menu links carry tenant ids. Names still show what kind of
      // link it was; the path alone identifies a stale one.
      searchKeys: describeSearchKeys(location.search),
    });
  }, [location.hash, location.pathname, location.search]);

  // Debug builds show a not-found-specific debug screen (its own heading, and
  // only the RouteNotFound beacon above — not RootPathDebugPage's, which would
  // double-fire and mislabel a non-root path as "Root Path Debug").
  if (isRouteDebugEnabled) {
    return <DebugRouteScreen heading="Route Not Found" />;
  }
  return <Navigate to={ROUTE_PATHS.MEMBER.INFORMATION} replace />;
};

// The LIFF entry link (https://liff.line.me/<liffId>?next=facilities-walk-in
// &tenantId=...) lands on the app root, with the query either restored directly
// or still wrapped in ?liff.state= on cold entry. Resolve `next` and redirect;
// without one, keep the old behavior (debug page / profile hub).
const RootEntryPage = () => {
  const params = unwrapLiffState(
    new URLSearchParams(globalThis.location.search),
  );
  const next = params.get("next")?.trim();
  if (next) {
    // Store the tenant from the entry URL before Navigate drops the query —
    // AuthContext's own ensureTenantId() effect runs after this navigation.
    ensureTenantId();
    const destination = getCallbackDestination(params);
    if (isPublicCallbackDestination(destination)) {
      // Walk-in guest flow — no member session needed, go straight there.
      return <Navigate to={destination} replace />;
    }
    // Member deep link: let CallbackPage resolve the session — logged-in goes
    // to the destination, logged-out to /login with `next` preserved so the
    // OTP login can still land on the screen the member tapped.
    return (
      <Navigate to={`/callback?next=${encodeURIComponent(next)}`} replace />
    );
  }
  if (isRouteDebugEnabled) {
    return <RootPathDebugPage />;
  }
  return <Navigate to={ROUTE_PATHS.MEMBER.INFORMATION} replace />;
};

const AppRoutesContent = () => {
  const location = useLocation();

  useEffect(() => {
    if (!isRouteDebugEnabled) return;

    const locationData = {
      pathname: location.pathname,
      hashKeys: describeHashKeys(location.hash),
      // Param names only: the LIFF return carries ?code=&state=&liff.state=
      // and rich-menu links carry tenant ids. Names still show what kind of
      // link it was; the path alone identifies a stale one.
      searchKeys: describeSearchKeys(location.search),
    };

    trackEvent("RouteRequested", locationData);
  }, [location.hash, location.pathname, location.search]);

  // React Router v7 runs navigations as transitions, and a transition that
  // suspends inside an already-mounted boundary keeps the previously committed
  // UI instead of showing the fallback. After a <Navigate> (e.g. /member →
  // /login when logged out) that previous UI is nothing — a blank page while
  // the next page's chunk downloads, forever if it never arrives. A fresh
  // boundary per ROUTE opts out of that (same fix as admin QTY-1856). The key
  // is the matched route pattern rather than the pathname, so moving between
  // ids of the same route (/trainer/details/1 → /trainer/details/2) keeps the
  // boundary — and the page's state — instead of remounting; those moves never
  // suspend anyway, the chunk is already loaded.
  const suspenseKey = matchedRoutePattern(location.pathname);

  return (
    <Suspense key={suspenseKey} fallback={<AppRouteLoading />}>
      <Routes>
        {routes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              route.route_type === RouteType.PRIVATE ? (
                <ProtectedRoute>
                  <route.component />
                </ProtectedRoute>
              ) : (
                <route.component />
              )
            }
          />
        ))}
        <Route path="/" element={<RootEntryPage />} />
        {/* Catch-all: an unknown path (stale rich-menu link, renamed route, a
            typo'd `next` deep-link) would otherwise render nothing — a blank
            white screen inside the LINE webview. NotFoundRedirect beacons it
            and sends the member to the profile hub (see its definition). */}
        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
    </Suspense>
  );
};

const AppRoutes = () => (
  <RouteErrorBoundary>
    <Suspense fallback={<AppRouteLoading />}>
      <AppRoutesContent />
    </Suspense>
  </RouteErrorBoundary>
);

export default AppRoutes;
